import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import DramaCategory from '@/pages/category/components/DramaCategory';
import NewsCategory from '@/pages/category/components/NewsCategory';
import { usePageMeta } from '@/hooks/usePageMeta';

interface CategoryConfig {
  title: string;
  description: string;
  metaTitle: string;
  metaDesc: string;
  metaKeywords: string;
  icon: string;
}

const configMap: Record<string, CategoryConfig> = {
  电视剧: {
    title: '电视剧剧集列表',
    description: '电视剧剧集列表，最新更新一目了然。按更新时间、类型标签与剧集状态筛选，快速找到你想追的那一部。',
    metaTitle: '电视剧 - 我妻子的一切电视剧资讯',
    metaDesc: '我妻子的一切电视剧分类页，汇总最新剧集更新、剧情简介与连载状态，支持按类型与更新时间筛选，随时掌握剧集动态。',
    metaKeywords: '我妻子的一切,电视剧列表,最新剧集,剧情介绍,剧集更新,连载,完结',
    icon: 'ri-movie-2-line',
  },
  花絮: {
    title: '花絮与幕后资讯',
    description: '最新电视剧花絮与幕后动态，涵盖片场纪实、主创专访与拍摄故事，看见镜头之外的精彩。',
    metaTitle: '花絮资讯 - 我妻子的一切电视剧资讯',
    metaDesc: '我妻子的一切花絮资讯分类页，提供最新电视剧片场花絮、幕后制作与主创专访，带你了解镜头背后的故事与拍摄细节。',
    metaKeywords: '我妻子的一切,花絮资讯,幕后花絮,片场纪实,主创专访,影视动态',
    icon: 'ri-newspaper-line',
  },
};

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const config = configMap[slug];

  usePageMeta(
    config ? config.metaTitle : '分类未找到 - 我妻子的一切',
    config ? config.metaDesc : '未找到对应的分类页面。',
    config ? config.metaKeywords : '',
  );

  if (!config) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-folder-unknow-line"
          title="分类不存在"
          description="你访问的分类暂时没有内容，返回首页看看其他推荐吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: slug }]} />

      <header className="mt-5 rounded-lg border border-background-200 bg-gradient-to-r from-primary-900 to-primary-700 px-6 py-8 md:px-10 md:py-10">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
            <i className={`${config.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              {config.title}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-200">
              {config.description}
            </p>
          </div>
        </div>
      </header>

      <div className="mt-6">
        {slug === '电视剧' ? <DramaCategory /> : <NewsCategory />}
      </div>
    </div>
  );
}